import { createSlice } from "@reduxjs/toolkit";
import {
  StudioDesign,
  StudioFormat,
  StudioPageInfo,
  StudioTemplate,
  StudioVideoJob,
} from "../../types/studioTypes";
import {
  fetchStudioDesigns,
  fetchStudioFormats,
  fetchStudioTemplates,
  fetchStudioVideoJobs,
} from "./StudioServices";

/**
 * The slice only holds data that is READ (GET): formats, templates, designs, video jobs.
 * Create / duplicate / delete / export / upload thunks have no cases here. The component
 * awaits them with .unwrap() and then refetches the list it changed.
 *
 * null = not loaded yet (StudioHome shows skeletons), [] = loaded but empty.
 */
export interface StudioState {
  formats: StudioFormat[];
  formatsLoading: boolean;
  formatsError: string | null;

  templates: StudioTemplate[] | null;
  templatesLoading: boolean;
  templatesError: string | null;

  designs: StudioDesign[] | null;
  designsPage: StudioPageInfo | null;
  designsLoading: boolean;
  designsError: string | null;

  jobs: StudioVideoJob[] | null;
  jobsLoading: boolean;
  jobsError: string | null;
}

const initialState: StudioState = {
  formats: [],
  formatsLoading: false,
  formatsError: null,

  templates: null,
  templatesLoading: false,
  templatesError: null,

  designs: null,
  designsPage: null,
  designsLoading: false,
  designsError: null,

  jobs: null,
  jobsLoading: false,
  jobsError: null,
};

const studioSlice = createSlice({
  name: "studio",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // formats
      .addCase(fetchStudioFormats.pending, (state) => {
        state.formatsLoading = true;
        state.formatsError = null;
      })
      .addCase(fetchStudioFormats.fulfilled, (state, action) => {
        state.formatsLoading = false;
        state.formats = action.payload;
      })
      .addCase(fetchStudioFormats.rejected, (state, action) => {
        state.formatsLoading = false;
        state.formatsError = action.payload ?? "Failed to fetch studio formats";
      })

      // templates
      .addCase(fetchStudioTemplates.pending, (state) => {
        state.templatesLoading = true;
        state.templatesError = null;
      })
      .addCase(fetchStudioTemplates.fulfilled, (state, action) => {
        state.templatesLoading = false;
        state.templates = action.payload;
      })
      .addCase(fetchStudioTemplates.rejected, (state, action) => {
        state.templatesLoading = false;
        state.templates = [];
        state.templatesError =
          action.payload ?? "Failed to fetch studio templates";
      })

      // designs ({ results, page })
      .addCase(fetchStudioDesigns.pending, (state) => {
        state.designsLoading = true;
        state.designsError = null;
      })
      .addCase(fetchStudioDesigns.fulfilled, (state, action) => {
        state.designsLoading = false;
        state.designs = action.payload?.results ?? [];
        state.designsPage = action.payload?.page ?? null;
      })
      .addCase(fetchStudioDesigns.rejected, (state, action) => {
        state.designsLoading = false;
        state.designs = [];
        state.designsError = action.payload ?? "Failed to fetch studio designs";
      })

      // video jobs (accepts either a plain array or { results })
      .addCase(fetchStudioVideoJobs.pending, (state) => {
        state.jobsLoading = true;
        state.jobsError = null;
      })
      .addCase(fetchStudioVideoJobs.fulfilled, (state, action) => {
        state.jobsLoading = false;
        const data = action.payload;
        state.jobs = Array.isArray(data) ? data : (data?.results ?? []);
      })
      .addCase(fetchStudioVideoJobs.rejected, (state, action) => {
        state.jobsLoading = false;
        state.jobs = [];
        state.jobsError = action.payload ?? "Failed to fetch video jobs";
      });
  },
});

export const selectStudioFormats = (state: { studio: StudioState }) =>
  state.studio.formats;

export const selectStudioTemplates = (state: { studio: StudioState }) =>
  state.studio.templates;

export const selectStudioDesigns = (state: { studio: StudioState }) =>
  state.studio.designs;

export const selectStudioJobs = (state: { studio: StudioState }) =>
  state.studio.jobs;

export default studioSlice.reducer;
